import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { api, errorMessage } from '../api';
import { PERMISSIONS, TASK_STATUSES, isTaskStatus, statusLabel } from '../constants';
import { useAsyncData } from '../useAsyncData';
import type {
  Task,
  TaskListResponse,
  TaskPatchBody,
  TaskPatchResponse,
  ToastKind,
  UsersResponse,
  UserWithRole,
} from '../types';
import { EmptyBox, ErrorBox, HintBox, Loading } from '../components/Feedback';
import { TaskCard } from '../components/TaskCard';
import { TaskCreateDialog } from '../components/TaskCreateDialog';
import { TaskDetailPanel } from '../components/TaskDetailPanel';

export interface BoardPageProps {
  permissions: string[];
  /** Zähler, der sich bei Realtime-Ereignissen erhöht. */
  revision: number;
  onToast: (kind: ToastKind, text: string) => void;
  onPermissionLost: (message: string) => void;
}

export function BoardPage({
  permissions,
  revision,
  onToast,
  onPermissionLost,
}: BoardPageProps): ReactNode {
  const canView = permissions.includes(PERMISSIONS.TASK_VIEW);
  const canCreate = permissions.includes(PERMISSIONS.TASK_CREATE);
  const canUpdate = permissions.includes(PERMISSIONS.TASK_UPDATE);
  const canAssign = permissions.includes(PERMISSIONS.TASK_ASSIGN);
  const canDelete = permissions.includes(PERMISSIONS.TASK_DELETE);

  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [hiddenStatuses, setHiddenStatuses] = useState<string[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  // Texteingabe leicht entprellen, damit nicht jeder Tastendruck eine Anfrage ist.
  useEffect(() => {
    const timer = window.setTimeout(() => setSearch(searchInput.trim()), 350);
    return () => window.clearTimeout(timer);
  }, [searchInput]);

  const activeStatuses = useMemo(
    () => TASK_STATUSES.filter((status) => !hiddenStatuses.includes(status)),
    [hiddenStatuses],
  );
  const statusParam = activeStatuses.length === TASK_STATUSES.length ? undefined : activeStatuses.join(',');

  const tasksState = useAsyncData(
    () =>
      api.get<TaskListResponse>('/api/tasks', {
        status: statusParam,
        search: search === '' ? undefined : search,
      }),
    [statusParam, search, revision],
    canView,
  );

  const usersState = useAsyncData(
    () => api.get<UsersResponse>('/api/users'),
    [revision],
    canView && (canAssign || canCreate),
  );

  const tasks = tasksState.data?.tasks ?? [];
  const users: UserWithRole[] = usersState.data?.users ?? [];
  const canSeeAll = tasksState.data?.canSeeAll ?? true;

  const reloadTasks = tasksState.reload;

  const patchTask = useCallback(
    async (id: string, patch: TaskPatchBody): Promise<boolean> => {
      try {
        await api.patch<TaskPatchResponse>(`/api/tasks/${id}`, patch);
        reloadTasks();
        return true;
      } catch (cause: unknown) {
        const message = errorMessage(cause);
        onToast('error', message);
        if (message.includes('Berechtigung') || message.includes('Recht')) onPermissionLost(message);
        return false;
      }
    },
    [onPermissionLost, onToast, reloadTasks],
  );

  const deleteTask = useCallback(
    (task: Task): void => {
      void (async () => {
        try {
          await api.del<{ ok: boolean }>(`/api/tasks/${task.id}`);
          onToast('success', `Aufgabe "${task.title}" gelöscht.`);
          setSelectedId((current) => (current === task.id ? null : current));
          reloadTasks();
        } catch (cause: unknown) {
          onToast('error', errorMessage(cause));
        }
      })();
    },
    [onToast, reloadTasks],
  );

  const selectedTask = selectedId === null ? null : tasks.find((task) => task.id === selectedId) ?? null;
  const unknownStatusTasks = tasks.filter((task) => !isTaskStatus(task.status));

  if (!canView) {
    return <ErrorBox message="Für das Board fehlt das Recht task.view." />;
  }

  return (
    <div className="page page-board">
      <div className="board-toolbar">
        <div className="toolbar-left">
          <label className="search-field">
            <span className="sr-only">Suche</span>
            <input
              type="search"
              placeholder="Aufgaben durchsuchen …"
              value={searchInput}
              onChange={(event) => setSearchInput(event.target.value)}
            />
          </label>

          <div className="chip-row" role="group" aria-label="Statusfilter">
            {TASK_STATUSES.map((status) => {
              const active = !hiddenStatuses.includes(status);
              return (
                <button
                  key={status}
                  type="button"
                  className={active ? 'chip chip-active' : 'chip'}
                  aria-pressed={active}
                  onClick={() =>
                    setHiddenStatuses((current) =>
                      current.includes(status)
                        ? current.filter((value) => value !== status)
                        : [...current, status],
                    )
                  }
                >
                  {statusLabel(status)}
                </button>
              );
            })}
          </div>
        </div>

        <div className="toolbar-right">
          <button
            type="button"
            className="button button-ghost button-small"
            onClick={() => reloadTasks()}
            disabled={tasksState.loading}
          >
            Aktualisieren
          </button>
          {canCreate && (
            <button type="button" className="button button-primary" onClick={() => setCreating(true)}>
              + Neue Aufgabe
            </button>
          )}
        </div>
      </div>

      {!canSeeAll && (
        <HintBox text="Du siehst nur dir zugewiesene und offene Aufgaben." />
      )}

      {tasksState.error !== null && (
        <ErrorBox message={tasksState.error} onRetry={() => reloadTasks()} />
      )}
      {usersState.error !== null && (
        <HintBox text={`Benutzerliste konnte nicht geladen werden: ${usersState.error}`} />
      )}
      {tasksState.loading && tasksState.data === null && <Loading label="Aufgaben laden …" />}

      {(!tasksState.loading || tasksState.data !== null) && (
        <div className="board-scroll">
          <div className="board">
            {TASK_STATUSES.filter((status) => !hiddenStatuses.includes(status)).map((status) => {
              const columnTasks = tasks.filter((task) => task.status === status);
              return (
                <section key={status} className="board-column">
                  <header className="board-column-head">
                    <h2>{statusLabel(status)}</h2>
                    <span className="count-pill">{columnTasks.length}</span>
                  </header>
                  <div className="board-column-body">
                    {columnTasks.length === 0 ? (
                      <p className="column-empty">Nichts hier.</p>
                    ) : (
                      columnTasks.map((task) => (
                        <TaskCard
                          key={task.id}
                          task={task}
                          users={users}
                          canUpdate={canUpdate}
                          canAssign={canAssign}
                          canDelete={canDelete}
                          onOpen={(value) => setSelectedId(value.id)}
                          onPatch={(id, patch) => void patchTask(id, patch)}
                          onDelete={deleteTask}
                        />
                      ))
                    )}
                  </div>
                </section>
              );
            })}

            {unknownStatusTasks.length > 0 && (
              <section className="board-column">
                <header className="board-column-head">
                  <h2>Unbekannter Status</h2>
                  <span className="count-pill">{unknownStatusTasks.length}</span>
                </header>
                <div className="board-column-body">
                  {unknownStatusTasks.map((task) => (
                    <TaskCard
                      key={task.id}
                      task={task}
                      users={users}
                      canUpdate={canUpdate}
                      canAssign={canAssign}
                      canDelete={canDelete}
                      onOpen={(value) => setSelectedId(value.id)}
                      onPatch={(id, patch) => void patchTask(id, patch)}
                      onDelete={deleteTask}
                    />
                  ))}
                </div>
              </section>
            )}
          </div>
        </div>
      )}

      {activeStatuses.length === 0 && <EmptyBox text="Alle Status sind ausgeblendet — Filter oben anpassen." />}
      {activeStatuses.length > 0 && !tasksState.loading && tasks.length === 0 && tasksState.error === null && (
        <EmptyBox
          text={
            search === ''
              ? 'Keine Aufgaben vorhanden. Lege die erste Aufgabe an.'
              : `Keine Aufgaben gefunden für "${search}".`
          }
        />
      )}

      {creating && (
        <TaskCreateDialog
          users={users}
          canAssign={canAssign}
          onClose={() => setCreating(false)}
          onCreated={(task) => {
            setCreating(false);
            setSelectedId(task.id);
            onToast('success', `Aufgabe "${task.title}" angelegt.`);
            reloadTasks();
          }}
        />
      )}

      {selectedTask !== null && (
        <TaskDetailPanel
          task={selectedTask}
          permissions={permissions}
          revision={revision}
          onClose={() => setSelectedId(null)}
          onPatch={patchTask}
          onDelete={deleteTask}
        />
      )}
    </div>
  );
}
